import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { connect, useDispatch } from "react-redux";
import { CSSTransition } from "react-transition-group";

import SVGIcon from "common/SVGIcon";
import { showModal, hideModal } from "redux/ws/actions";
import { ownPropsPriority } from "redux/utils";

export const createModal = (Component, mapState = null, mapDispatch = {}) =>
    connect(mapState, { hideModal, ...mapDispatch }, ownPropsPriority)(Component);

export const useCloseModal = () => {
    const dispatch = useDispatch();
    return useCallback(() => dispatch(hideModal()), []);
};

const FADE_TIME_MS = 250;

const mapStateToProps = (state) => ({
    modals: state.ws.modals,
});

const mapDispatchToProps = {
    showModal,
    hideModal,
};

class ModalContainer extends React.PureComponent {
    static propTypes = {
        modals: PropTypes.arrayOf(PropTypes.node),
        showModal: PropTypes.func.isRequired,
        hideModal: PropTypes.func.isRequired,
    };

    static defaultProps = {
        modals: [],
    };

    static getDerivedStateFromProps({ modals }, state) {
        const modalCount = modals.length;
        const oldCount = state.modalCount;
        let { hide } = state;

        if (modalCount === 0 && oldCount > 0) {
            hide = true;
        } else if (modalCount > 0 && oldCount === 0) {
            hide = false;
        }

        return {
            hide,
            modalCount,
        };
    }

    state = {
        hide: true,
        modalCount: 0,
    };

    componentDidMount() {
        document.addEventListener("click", this.onOpenClick);
    }

    componentWillUnmount() {
        document.removeEventListener("click", this.onOpenClick);
    }

    onOpenClick = (event) => {
        if (!event.target.dataset) {
            return;
        }

        const { modal } = event.target.dataset;
        if (!modal) {
            return;
        }

        event.preventDefault();

        const $modal = document.querySelector(modal);
        if ($modal) {
            /* eslint-disable react/no-danger */
            this.props.showModal(
                <div className="portal-modal portal-modal-small">
                    <SVGIcon icon="close" onClick={this.props.hideModal} />
                    <div dangerouslySetInnerHTML={{ __html: $modal.innerHTML }} />
                </div>,
            );
            /* eslint-enable */
        }
    };

    render() {
        const { modals } = this.props;

        // Display all modals so they can maintain internal state,
        // but only the :last-child is visible
        const nodes = modals.map((modal, idx) => React.cloneElement(modal, { key: idx }));

        return (
            <CSSTransition
                mountOnEnter
                unmountOnExit
                in={!this.state.hide}
                classNames="fade250"
                timeout={FADE_TIME_MS}
            >
                <div className="ws-modal-container">
                    <div className="ws-modal-cover">
                        <div className="ws-modals">{nodes}</div>
                    </div>
                </div>
            </CSSTransition>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps, ownPropsPriority)(ModalContainer);
